import {
    Button,
    ILocalizedError,
    IPaymentAccountStatus,
    IRestaurant,
    Pill,
    Spinner,
    TPillTone,
    useConfirmContext,
} from '@localina/core';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
    useCreatePaymentAccountLinkMutation,
    useCreatePaymentAccountMutation,
    usePaymentAccount,
} from '../../../api/queries/payments';
import { Drawer } from '../../Drawer';

// No-Show Protection, part 1: the restaurant connects its own payment account once (Settings -> Integrations ->
// Payments). Payouts, refunds and chargebacks run through that account, not through localsearch. The account
// status is always read live from the provider.

interface IProps {
    isOpen: boolean;
    restaurant: IRestaurant;
    onClose: () => void;
}

type TAccountState = 'notConnected' | 'setupOpen' | 'inReview' | 'active';

export const getPaymentAccountState = (account?: IPaymentAccountStatus): TAccountState => {
    if (!account?.connected) {
        return 'notConnected';
    }
    if (!account.onboardingComplete) {
        return 'setupOpen';
    }
    return account.readyToProcessPayments ? 'active' : 'inReview';
};

export const PAYMENT_ACCOUNT_STATE_TONE: Record<TAccountState, TPillTone> = {
    notConnected: 'neutral',
    setupOpen: 'warn',
    inReview: 'warn',
    active: 'good',
};

export const PaymentsIntegrationView: React.FC<IProps> = (props) => {
    const { t } = useTranslation();
    const { snackbar } = useConfirmContext();
    const [searchParams, setSearchParams] = useSearchParams();

    // react-query refetches it when the window gets focus again, e.g. back from the provider's tab
    const accountQuery = usePaymentAccount({ enabled: props.isOpen });
    const account = accountQuery.data;
    const state = getPaymentAccountState(account);
    const provider = t(`integrations.items.payments.providers.${account?.provider ?? 'stripe'}`);

    const createAccountMutation = useCreatePaymentAccountMutation();
    const createLinkMutation = useCreatePaymentAccountLinkMutation();
    const isRedirecting = createAccountMutation.isLoading || createLinkMutation.isLoading;

    const goToOnboarding = () =>
        createLinkMutation.mutate(undefined, {
            // onboarding links are single-use, so the browser goes straight to the provider
            onSuccess: (link) => window.location.assign(link.url),
            onError: (error) =>
                snackbar({
                    msg: t(paymentsErrorKey(error, 'integrations.items.payments.errors.onboarding')),
                    severity: 'error',
                }),
        });

    const connect = () => {
        if (account?.connected) {
            goToOnboarding();
            return;
        }
        // the account is created with the restaurant's details; the rest is collected by the provider
        createAccountMutation.mutate(
            { displayName: props.restaurant.info.name, contactEmail: props.restaurant.info.email },
            {
                onSuccess: goToOnboarding,
                onError: (error) =>
                    snackbar({
                        msg: t(paymentsErrorKey(error, 'integrations.items.payments.errors.connect')),
                        severity: 'error',
                    }),
            },
        );
    };

    // leaving for the provider and coming back with the browser's back button restores this page from the
    // back/forward cache as it was, i.e. still "redirecting": start over and reload the live status
    useEffect(() => {
        const onPageShow = (event: PageTransitionEvent) => {
            if (event.persisted) {
                createAccountMutation.reset();
                createLinkMutation.reset();
                void accountQuery.refetch();
            }
        };
        window.addEventListener('pageshow', onPageShow);
        return () => window.removeEventListener('pageshow', onPageShow);
    }, []);

    // back from the provider (see PaymentAccountService.createOnboardingLink):
    //  ?payments=return  -> reload the live status
    //  ?payments=refresh -> the link expired or was already used: continue with a new one
    const handledRedirect = useRef(false);
    useEffect(() => {
        const redirect = searchParams.get('payments');
        if (!props.isOpen || !redirect || handledRedirect.current) {
            return;
        }
        handledRedirect.current = true;
        setSearchParams({}, { replace: true });
        if (redirect === 'refresh') {
            goToOnboarding();
        } else {
            void accountQuery.refetch();
        }
    }, [props.isOpen, searchParams]);

    return (
        <Drawer
            className="payments-view"
            open={props.isOpen}
            onClose={props.onClose}
            title={t('integrations.items.payments.title')}
            subtitle={t('integrations.items.payments.drawerSubtitle')}
        >
            <div className="integration payments">
                <p className="payments__lead">{t('integrations.items.payments.lead')}</p>

                {accountQuery.isError ? (
                    <PaymentsError error={accountQuery.error} />
                ) : (
                    <div className="payments__panel">
                        <div className="payments__panel-title">
                            <span>{t('integrations.items.payments.account.title')}</span>
                            {account && (
                                <Pill
                                    tone={PAYMENT_ACCOUNT_STATE_TONE[state]}
                                    label={t(`integrations.items.payments.state.${state}`)}
                                />
                            )}
                        </div>
                        {account && state === 'notConnected' && (
                            <div className="payments__panel-body">
                                <dl className="payments__facts">
                                    <dt>{t('integrations.items.payments.account.provider')}</dt>
                                    <dd>{provider}</dd>
                                    <dt>{t('integrations.items.payments.account.payout')}</dt>
                                    <dd>{t('integrations.items.payments.account.payoutValue')}</dd>
                                    <dt>{t('integrations.items.payments.account.fees')}</dt>
                                    <dd>{t('integrations.items.payments.account.feesValue', { provider })}</dd>
                                </dl>
                                <p className="payments__hint">{t('integrations.items.payments.account.needed')}</p>
                                <Button
                                    label={t('integrations.items.payments.account.connect', { provider })}
                                    disabled={isRedirecting}
                                    onClick={connect}
                                />
                            </div>
                        )}
                        {account && state !== 'notConnected' && (
                            <div className="payments__panel-body">
                                <dl className="payments__facts">
                                    <dt>{t('integrations.items.payments.account.provider')}</dt>
                                    <dd>
                                        {provider}
                                        {account.accountId && (
                                            <span className="payments__mono"> · {account.accountId}</span>
                                        )}
                                    </dd>
                                    {account.displayName && (
                                        <>
                                            <dt>{t('integrations.items.payments.account.holder')}</dt>
                                            <dd>{account.displayName}</dd>
                                        </>
                                    )}
                                    <dt>{t('integrations.items.payments.account.chargesPossible')}</dt>
                                    <dd className={account.readyToProcessPayments ? 'yes' : 'no'}>
                                        {t(
                                            account.readyToProcessPayments
                                                ? 'integrations.items.payments.account.yes'
                                                : 'integrations.items.payments.account.no',
                                        )}
                                    </dd>
                                    <dt>{t('integrations.items.payments.account.methods')}</dt>
                                    <dd>
                                        {account.availableMethods?.length
                                            ? account.availableMethods
                                                  .map((method) => t(`integrations.items.payments.methods.${method}`))
                                                  .join(', ')
                                            : '–'}
                                    </dd>
                                </dl>
                                {state === 'active' && (
                                    <div className="payments__note ok">
                                        {t('integrations.items.payments.state.activeHint')}
                                    </div>
                                )}
                                {state === 'setupOpen' && (
                                    <div className="payments__note warn">
                                        {t('integrations.items.payments.state.setupOpenHint', { provider })}
                                    </div>
                                )}
                                {state === 'inReview' && (
                                    <div className="payments__note warn">
                                        {t('integrations.items.payments.state.inReviewHint', { provider })}
                                    </div>
                                )}
                                {state === 'setupOpen' ? (
                                    <Button
                                        label={t('integrations.items.payments.account.continue', { provider })}
                                        disabled={isRedirecting}
                                        onClick={goToOnboarding}
                                    />
                                ) : (
                                    <div className="payments__links">
                                        {account.dashboardUrl && (
                                            <a
                                                className="payments__link"
                                                href={account.dashboardUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {t('integrations.items.payments.account.dashboard', { provider })}
                                            </a>
                                        )}
                                        <button
                                            type="button"
                                            className="payments__link"
                                            disabled={isRedirecting}
                                            onClick={goToOnboarding}
                                        >
                                            {t('integrations.items.payments.account.update', { provider })}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {state !== 'active' && (
                    <div className="payments__note neutral">{t('integrations.items.payments.shiftHint')}</div>
                )}
            </div>
            {(accountQuery.isInitialLoading || isRedirecting) && <Spinner />}
        </Drawer>
    );
};

const paymentsErrorKey = (error: unknown, fallback: string) => {
    const status = (error as ILocalizedError | undefined)?.status;
    if (status === 403) {
        return 'integrations.items.payments.errors.forbidden';
    }
    return status === 503 ? 'integrations.items.payments.errors.notConfigured' : fallback;
};

const PaymentsError: React.FC<{ error: ILocalizedError }> = ({ error }) => {
    const { t } = useTranslation();
    return (
        <div className="payments__note warn">
            {t(paymentsErrorKey(error, 'integrations.items.payments.errors.generic'))}
        </div>
    );
};
